import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ClipboardCheck, Sparkles } from "lucide-react";
import { scrollToSection, EASE } from "./Reveal";
import { PORTRAIT } from "../data/content";

const lines = [
  { text: "Fashion strategy,", cls: "text-[#1d1d1f]" },
  { text: "made commercially", cls: "text-[#1d1d1f]" },
  { text: "beautiful.", cls: "font-serif-editorial italic font-light text-[#0066cc]" },
];

export default function Hero() {
  const frameRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: frameRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 140]);

  return (
    <section id="top" ref={frameRef} className="relative pt-28 sm:pt-36 pb-16 sm:pb-24 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(60% 50% at 78% 30%, rgba(0,102,204,0.07) 0%, rgba(0,0,0,0) 70%), radial-gradient(45% 40% at 15% 80%, rgba(212,175,55,0.06) 0%, rgba(0,0,0,0) 70%)",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 grid lg:grid-cols-[1.15fr_0.85fr] gap-12 lg:gap-16 items-center">
        <motion.div style={{ y: textY }} className="flex flex-col items-start gap-7">
          <p className="mask-line text-xs sm:text-sm uppercase tracking-[0.22em] font-semibold text-[#86868b]">
            <span style={{ animationDelay: "0.05s" }} className="flex items-center gap-2">
              <span className="w-8 h-px bg-[#d4af37] inline-block" style={{ animation: "none", transform: "none" }} />
              Lead Fashion Design Consultant · New Delhi
            </span>
          </p>

          <h1 className="text-[2.6rem] leading-[1.05] sm:text-6xl lg:text-[4.6rem] font-semibold tracking-tight">
            {lines.map((l, i) => (
              <span key={i} className="mask-line">
                <span style={{ animationDelay: `${0.18 + i * 0.14}s`, color: l.cls ? undefined : undefined }}>
                  <span className={l.cls}>{l.text}</span>
                </span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.9, ease: EASE }}
            className="text-lg sm:text-xl text-[#86868b] leading-relaxed max-w-xl"
          >
            Shaping scalable design functions for apparel manufacturers and B2B and B2C brands —
            from Design Head at ITC, Zara and H&amp;M to Creative Director at FableStreet.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9, ease: EASE }}
            className="flex flex-wrap items-center gap-3"
          >
            <button
              data-testid="hero-cta-booking"
              onClick={() => scrollToSection("#booking")}
              className="group px-6 py-3.5 rounded-full bg-[#0066cc] text-white font-medium text-sm sm:text-base hover:bg-[#0077ed] transition-all active:scale-[0.98] shadow-md flex items-center gap-2"
            >
              Book a discovery call
              <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-assessment"
              onClick={() => scrollToSection("#assessment")}
              className="px-6 py-3.5 rounded-full bg-white hover:bg-[#e8e8ed] text-[#1d1d1f] font-medium text-sm sm:text-base transition-all flex items-center gap-2 border border-[#d2d2d7]"
            >
              <ClipboardCheck size={17} className="text-[#0066cc]" />
              Take the brand assessment
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 1 }}
            className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-2 text-[13px] text-[#86868b]"
          >
            {["Ex-Zara & H&M Product Development", "Ex-NIFT Delhi", "₹50 Cr FableStreet design mandate"].map((t) => (
              <span key={t} className="flex items-center gap-1.5">
                <Sparkles size={13} className="text-[#d4af37]" /> {t}
              </span>
            ))}
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 1.04, filter: "blur(8px)" }}
          animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
          transition={{ delay: 0.5, duration: 1.3, ease: EASE }}
          className="relative"
        >
          <motion.div style={{ y: imgY }} className="spotlight-frame shadow-[0_30px_60px_rgba(0,0,0,0.12)]">
            <img
              src={PORTRAIT}
              alt="Sundeepa Bamval, lead fashion design consultant"
              className="w-full h-[420px] sm:h-[520px] object-cover object-top"
              loading="eager"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.9, ease: EASE }}
            className="absolute -bottom-6 -left-4 sm:-left-8 glass-card rounded-2xl px-5 py-4 shadow-[0_16px_40px_rgba(0,0,0,0.10)]"
            data-testid="hero-badge-card"
          >
            <p className="text-2xl font-semibold text-[#1d1d1f] tracking-tight">Ex-Zara &amp; H&amp;M</p>
            <p className="text-[13px] text-[#86868b] max-w-[190px] leading-snug">
              a decade of product development, now advising Indian brands
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
